import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {AdminRolesSelect} from 'src/admin-rights-service/modules/admin-profile/sections/admin-role-select';
import {useRolesPermissions} from 'src/admin-rights-service/modules/roles-page/useRolesPermissions';
import {BasicPage} from 'src/components/basic-page';
import {DateRangeInput, FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl, ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import Button from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {useShown} from '@pb/kit-hooks-use-shown';


import {NotificationTopic} from '../components/notification-topic';
import {PREVIEW_MODAL_WIDTH, PreviewModal} from '../components/preview-modal';
import {NotificationContent} from '../content-builder/notification-content';

import {useCreateNotification} from './useCreateNotification';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {ProfileRecord} from 'src/openapi/admin/models/ProfileRecord';

const CreateNotification = () => {
    const navigate = useNavigate();
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const {shown: previewShown, onClick: showPreview, onClose: closePreview} = useShown();

    const [busy, setBusy] = useState(false);

    const {
        proceed,
        setIsChunkChanged,
    } = useConfirmationChunk({chunkName: 'create-admin-notification', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        navigate('/internal-notification');
    }});

    const {isLoading: isRolesPermissionsLoading, data: roles} = useRolesPermissions();

    const {
        isLoading: isNotificationLoading,
        loadingError: notificationLoadingError,
        isChanged: isNotificationChanged,
        data: notification,
        setData: setNotification,
        saveChanges: saveNotification,
    } = useCreateNotification();

    useEffect(() => {
        setIsChunkChanged(isNotificationChanged);
    }, [isNotificationChanged, setIsChunkChanged]);

    const backToActiveAlerts = useCallback(() => {
        navigate('/internal-notification');
    }, [navigate]);

    const setNotificationContent = useCallback((content: string) => {
        setNotification(prev => ({...prev, content}));
    }, [setNotification]);

    const changeTopic = useCallback((topic: string) => {
        setNotification(prev => ({...prev, topic}));
    }, [setNotification]);

    const setNotificationTitle = useCallback((title: string) => {
        setNotification(prev => ({...prev, title}));
    }, [setNotification]);

    const changeStartDate = useCallback((startDate: string) => {
        setNotification(prev => ({...prev, startDate}));
    }, [setNotification]);

    const changeEndDate = useCallback((endDate: string) => {
        setNotification(prev => ({...prev, endDate}));
    }, [setNotification]);

    const changeSelectedRoles = useCallback((selectedRoles: ProfileRecord[] | null) => {
        setNotification(prev => ({
            ...prev,
            adminProfileIds: selectedRoles?.map(role => role.profileId) || [],
        }));
    }, [setNotification]);

    const onSaveClick = useCallback(async () => {
        setBusy(true);
        const error = await saveNotification() as string | undefined;
        setBusy(false);

        if (error) {
            setErrorMessage(error);
        } else {
            proceed();
        }
    }, [saveNotification, proceed, setErrorMessage]);

    const selectedRoles = useMemo(() => {
        return roles.filter(role => notification.adminProfileIds?.includes(role.profileId));
    }, [notification.adminProfileIds, roles]);

    const isPreviewDisabled = !notification?.content || !notification?.title;
    const isCreateDisabled =
        !notification.title ||
        !notification.topic ||
        !notification.content ||
        !notification.startDate ||
        !notification.endDate;

    return (
        <Skeleton shown={isNotificationLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler
                serverError={notificationLoadingError}
                accessRight={ACCESS_RIGHTS.INTERNAL_NOTIFICATION_BUILDER as AccessRight}>
                <BasicPage
                    title="Create Notification"
                    buttonsBlock={(
                        <Group direction="h">
                            <Button variant="Type.TERTIARY" onClick={backToActiveAlerts}>
                                Cancel
                            </Button>
                            <Button variant="Type.SECONDARY" onClick={showPreview} disabled={isPreviewDisabled}>
                                Preview
                            </Button>
                            <Button variant="Type.PRIMARY" disabled={isCreateDisabled} onClick={onSaveClick} busy={busy}>
                                Create
                            </Button>
                        </Group>
                    )}>
                    <Group direction="h" gap="s" style={{margin: '10px 25px'}}>
                        <Group id="notification-builder">
                            <NotificationContent
                                value={notification.content || ''}
                                setValue={setNotificationContent}
                            />
                        </Group>
                        <Group style={{margin: '10px 25px'}} direction="v">
                            <FormInput
                                id="notification-title-input"
                                placeholder="Enter notification title"
                                label={<RequiredMark label="Notification Title"/>}
                                value={notification.title || ''}
                                onBlur={setNotificationTitle}
                                width={400}
                                maxLength={64}
                            />
                            <NotificationTopic
                                topic={notification.topic}
                                setTopic={changeTopic}
                            />
                            <Group direction="v" gap="s">
                                <Label><RequiredMark label="Activity Date"/></Label>
                                <DateRangeInput
                                    id="notification-start-date"
                                    label={<>Starts</>}
                                    width={400}
                                    clearable={true}
                                    preventPastDate={true}
                                    startDate={notification.startDate}
                                    endDate={notification.endDate}
                                    selectRangeType="start"
                                    onChange={changeStartDate}
                                />
                                <ValidatedControl name="notification-end-date">
                                    {(invalid: boolean) => (
                                        <DateRangeInput
                                            id="notification-end-date"
                                            label={<>Ends&nbsp;&nbsp;</>}
                                            width={400}
                                            invalid={invalid}
                                            clearable={true}
                                            preventPastDate={true}
                                            startDate={notification.startDate}
                                            endDate={notification.endDate}
                                            selectRangeType="end"
                                            onChange={changeEndDate}
                                        />
                                    )}
                                </ValidatedControl>
                            </Group>
                            <AdminRolesSelect
                                isLoading={isRolesPermissionsLoading}
                                selectedRoles={selectedRoles}
                                roles={roles}
                                changeRoles={changeSelectedRoles}
                            />
                        </Group>
                    </Group>
                    <Dialog
                        shown={previewShown}
                        setShown={closePreview}
                        wrapperProps={{style: {width: PREVIEW_MODAL_WIDTH}}}>
                        <PreviewModal
                            notification={notification}
                            onClose={closePreview}
                        />
                    </Dialog>
                </BasicPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};

export const CreateNotificationWrapper = () => (
    <ValidationContextProvider>
        <CreateNotification/>
    </ValidationContextProvider>
);
